import {
  FlexColumn,
  FlexResponsive,
  FlexRow,
  LabeledInput,
  Loading,
  StyledBanner,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Delete } from 'baseui/icon';
import { LabelMedium } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { REQUIRED_FIELD_TEXT, VELID_FIELD_TEXT } from '../../constants/app';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { useStyles } from '../../hooks/useStyles';
import { getAsset } from '../../services/assetService';
import {
  editInstallationState,
  editInstallationTouchedState,
  editInstallationValidState,
} from '../../storage/Installations';
import { Asset } from '../../types/asset';
import { Brand } from '../../types/brand';
import { InstallationRequest, InstallationRequestTouched } from '../../types/installation';
import { JobTypeCategory } from '../../types/job';
import { SubBrandCore } from '../../types/subBrand';
import { splitFullName } from '../../utils/installations';
import { SearchAssetData } from '../shared/SearchAssetData';
import { BrandSelect } from '../shared/selects/BrandSelect';
import { CampaignSelect } from '../shared/selects/CampaignSelect';
import JobTypeCategorySelect from '../shared/selects/JobTypeCategorySelect';
import { SubBrandSelect } from '../shared/selects/SubBrandSelect';
import { InstallationClientForm } from './InstallationClientForm';
import { InstallationContactForm } from './InstallationContactForm';

type Props = {
  defaultValues?: InstallationRequest;
  defaultBrand?: Brand;
};

export function InstallationForm({ defaultValues, defaultBrand }: Readonly<Props>) {
  const { classes, css, theme } = useStyles();
  const [form, setForm] = useRecoilState(editInstallationState);
  const resetForm = useResetRecoilState(editInstallationState);
  const [formTouched, setFormTouched] = useRecoilState(editInstallationTouchedState);
  const resetFormTouched = useResetRecoilState(editInstallationTouchedState);
  const formValid = useRecoilValue(editInstallationValidState);
  const [selectedAsset, setSelectedAsset] = useState<Asset>();
  const [selectedBrand, setSelectedBrand] = useState<Brand | undefined>(defaultBrand);
  const [selectedSubBrand, setSelectedSubBrand] = useState<SubBrandCore | undefined>(undefined);
  const [manualForm, setManualForm] = useState<boolean>(false);
  const shouldHaveAsset = !!defaultValues && form.category !== JobTypeCategory.installation;
  const [shouldHaveAssetError, setShouldHaveAssetError] = useState('');
  const { isSmall } = useBreakpoints();
  const { loading: searchingAsset, startLoading: startSearchingAsset, stopLoading: stopSearchingAsset } = useLoading();

  useEffect(() => {
    const loadAsset = async (assetId: string) => {
      startSearchingAsset();
      const { asset, error } = await getAsset(assetId);
      if (asset && !error) {
        setSelectedAsset(asset);
      } else {
        setShouldHaveAssetError('No se pudo cargar el activo, ingrésalo manualmente.');
      }
      stopSearchingAsset();
    };

    const assetId = defaultValues?.assetId;
    if (shouldHaveAsset && assetId) {
      loadAsset(assetId);
    }
  }, [shouldHaveAsset, defaultValues?.assetId]);

  useEffect(() => {
    if (defaultValues) {
      setForm(defaultValues);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (selectedAsset) {
      setManualForm(true);
    }
    const splitName = splitFullName(
      selectedAsset?.client?.installationContact?.fullName ?? selectedAsset?.client?.contact?.fullName ?? ''
    );
    setSelectedBrand(selectedAsset?.vehicle?.brand);
    setSelectedSubBrand(selectedAsset?.vehicle?.subBrand);
    setForm((prev) => ({
      ...prev,
      assetId: selectedAsset?._id,
      brandId: selectedAsset?.vehicle?.brand?._id,
      subBrandId: selectedAsset?.vehicle?.subBrand?._id,
      model: selectedAsset?.vehicle?.year,
      vin: selectedAsset?.vehicle?.vin ?? '',
      licensePlate: selectedAsset?.vehicle?.licensePlate ?? '',
      policyNumber: selectedAsset?.insuranceNumber ?? '',
      label: selectedAsset?.label ?? '',
      client: {
        clientName: selectedAsset?.client.name ?? '',
        name: splitName.name !== '' ? splitName.name : selectedAsset?.client?.name ?? '',
        lastName: splitName.lastName ?? '',
        motherName: splitName.motherName ?? '',
        email: selectedAsset?.client?.installationContact?.email ?? selectedAsset?.client?.contact?.email ?? '',
        phone: selectedAsset?.client?.installationContact?.phone ?? selectedAsset?.client?.contact?.phone ?? '',
        rfc: selectedAsset?.client?.rfc ?? '',
      },
    }));
    resetFormTouched();
  }, [selectedAsset]);

  useEffect(() => {
    if (form?.category === JobTypeCategory.installation && !!selectedAsset) {
      setManualForm(false);
      setSelectedAsset(undefined);
      resetFormData();
    } else if (form?.category !== JobTypeCategory.installation && !!selectedAsset) {
      setSelectedBrand(selectedAsset.vehicle?.brand);
      setForm((prev) => {
        const splitName = splitFullName(
          selectedAsset?.client?.installationContact?.fullName ?? selectedAsset?.client?.contact?.fullName ?? ''
        );
        return {
          ...prev,
          brandId: selectedAsset.vehicle?.brand?._id,
          subBrandId: selectedAsset.vehicle?.subBrand?._id,
          model: selectedAsset.vehicle?.year,
          vin: selectedAsset.vehicle?.vin ?? '',
          licensePlate: selectedAsset.vehicle?.licensePlate ?? '',
          client: {
            clientName: selectedAsset.client.name,
            name: splitName.name !== '' ? splitName.name : selectedAsset?.client?.name ?? '',
            lastName: splitName.lastName ?? '',
            motherName: splitName.motherName ?? '',
            email: selectedAsset?.client?.installationContact?.email ?? selectedAsset?.client?.contact?.email ?? '',
            phone: selectedAsset?.client?.installationContact?.phone ?? selectedAsset?.client?.contact?.phone ?? '',
            rfc: selectedAsset?.client?.rfc ?? '',
          },
        };
      });
      resetFormTouched();
    }
  }, [form?.category]);

  const onTouched = (key: keyof InstallationRequestTouched) => {
    setFormTouched((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  const onChangeInput = (value: any, field: keyof InstallationRequest) => {
    setForm((prev) => {
      return {
        ...prev!,
        [field]: value,
      };
    });
  };

  const resetFormData = () => {
    resetForm();
    resetFormTouched();
    if (defaultValues) {
      setForm(defaultValues);
    }
    if (defaultBrand) {
      setSelectedBrand(defaultBrand);
    }
  };

  const canSearch = !shouldHaveAsset && form.category !== JobTypeCategory.installation;
  const showSearch = canSearch && !manualForm && !selectedAsset;
  const showForm = useMemo(() => {
    if (form.category === JobTypeCategory.installation || manualForm || shouldHaveAsset) return true;
    if ((form.category === JobTypeCategory.revision || form.category === JobTypeCategory.uninstall) && !!selectedAsset)
      return true;
    return false;
  }, [form?.category, selectedAsset, manualForm, shouldHaveAsset]);

  return (
    <FlexColumn>
      {searchingAsset && (
        <Block
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            minHeight: '40vh',
          })}
        >
          <Loading />
        </Block>
      )}
      {!searchingAsset && (
        <>
          {shouldHaveAssetError && (
            <StyledBanner
              // title=""
              kind="negative"
              action={{
                label: 'label',
                icon: () => <Delete />,
                onClick: () => {
                  setShouldHaveAssetError('');
                },
              }}
            >
              {shouldHaveAssetError}
            </StyledBanner>
          )}
          <FlexRow
            classNames={css({ alignItems: 'end' })}
            gap={theme.sizing.scale300}
          >
            <JobTypeCategorySelect
              label="Categoría"
              selectedId={form?.category}
              onChangeId={(value) => onChangeInput(value, 'category')}
              onBlur={() => onTouched('categoryTouched')}
              error={formTouched.categoryTouched && !formValid.category && REQUIRED_FIELD_TEXT}
              disabled={!!defaultValues}
              required
            />
            <CampaignSelect
              selectedCampaignId={form.insuranceCompany}
              onChange={(value) => {
                onChangeInput(value, 'insuranceCompany');
              }}
              onBlur={() => onTouched('insuranceCompanyTouched')}
              error={formTouched.insuranceCompanyTouched && !formValid.insuranceCompany && REQUIRED_FIELD_TEXT}
              required
            />
            {canSearch && (
              <StyledButton
                kind="tertiary"
                size="compact"
                onClick={() => {
                  if (manualForm) {
                    setManualForm(false);
                    setSelectedAsset(undefined);
                  } else {
                    setManualForm(true);
                  }
                }}
              >
                {manualForm ? 'Nueva busqueda' : 'Llenar manualmente'}
              </StyledButton>
            )}
          </FlexRow>
          {showSearch && (
            <SearchAssetData
              onSelectedAsset={setSelectedAsset}
              onCancel={() => setManualForm(true)}
            />
          )}
          {showForm && (
            <FlexResponsive>
              <FlexColumn
                classNames={css({
                  flex: '1',
                })}
              >
                <LabelMedium
                  className={classes.highlightDataHeaderClass}
                  margin={isSmall ? '16px 0px 0px' : '0px'}
                >
                  Datos del vehículo
                </LabelMedium>
                <BrandSelect
                  label="Marca"
                  selectedBrandId={form?.brandId}
                  onChangeBrandId={(brandId) => onChangeInput(brandId, 'brandId')}
                  onChangeBrand={(brand) => {
                    setSelectedBrand(brand);
                  }}
                  onBlur={() => onTouched('brandTouched')}
                  error={formTouched.brandTouched && !formValid.brandId && REQUIRED_FIELD_TEXT}
                  brand={selectedBrand}
                  required
                />
                <SubBrandSelect
                  label="Submarca"
                  selectedBrandId={form?.brandId}
                  selectedSubBrandId={form?.subBrandId}
                  onChangeSubBrand={(subBrandId) => onChangeInput(subBrandId, 'subBrandId')}
                  onBlur={() => onTouched('subBrandTouched')}
                  error={formTouched.subBrandTouched && !formValid.subBrandId && REQUIRED_FIELD_TEXT}
                  disabled={!form?.brandId}
                  subBrand={selectedSubBrand}
                  required
                />
                <LabeledInput
                  label="Modelo"
                  placeholder="Ingresa el modelo"
                  value={form.model || ''}
                  type="number"
                  onChange={(value) => {
                    onChangeInput(value, 'model');
                  }}
                  onBlur={() => onTouched('modelTouched')}
                  error={
                    formTouched.modelTouched &&
                    !formValid.model &&
                    (!form.model ? REQUIRED_FIELD_TEXT : VELID_FIELD_TEXT())
                  }
                  required
                />
                <LabeledInput
                  label="Número de motor"
                  placeholder="Ingresa el número del motor"
                  value={form.engineNumber}
                  onChange={(value) => {
                    onChangeInput(value, 'engineNumber');
                  }}
                  onBlur={() => onTouched('engineNumberTouched')}
                  error={formTouched.engineNumberTouched && !formValid.engineNumber && REQUIRED_FIELD_TEXT}
                  required
                />
                <LabeledInput
                  label="Poliza"
                  placeholder="Ingresa el número de poliza"
                  value={form.policyNumber}
                  onChange={(value) => {
                    onChangeInput(value, 'policyNumber');
                  }}
                />
                <LabeledInput
                  label="Número económico"
                  placeholder="Ingresa el número económico"
                  value={form.label}
                  onChange={(value) => {
                    onChangeInput(value, 'label');
                  }}
                  onBlur={() => onTouched('labelTouched')}
                  error={formTouched.labelTouched && !formValid.label && REQUIRED_FIELD_TEXT}
                  required
                />
                <LabeledInput
                  label="Vin"
                  placeholder="Ingresa el número de serie"
                  value={form.vin}
                  onChange={(value) => {
                    onChangeInput(value, 'vin');
                  }}
                  onBlur={() => onTouched('vinTouched')}
                  error={formTouched.vinTouched && !formValid.vin && REQUIRED_FIELD_TEXT}
                  required
                />
                <LabeledInput
                  label="Placa"
                  placeholder="Ingresa el número de placas"
                  value={form.licensePlate}
                  onChange={(value) => {
                    onChangeInput(value, 'licensePlate');
                  }}
                  onBlur={() => onTouched('licensePlateTouched')}
                  error={formTouched.licensePlateTouched && !formValid.licensePlate && REQUIRED_FIELD_TEXT}
                  required
                />
              </FlexColumn>
              <FlexColumn
                classNames={css({
                  flex: '1',
                  justifyContent: 'space-between',
                })}
              >
                <FlexColumn>
                  <LabelMedium
                    className={classes.highlightDataHeaderClass}
                    margin={isSmall ? '16px 0px 0px' : '0px'}
                  >
                    Datos del cliente
                  </LabelMedium>
                  <InstallationClientForm />
                </FlexColumn>
                <FlexColumn>
                  <LabelMedium
                    className={classes.highlightDataHeaderClass}
                    margin={isSmall ? '16px 0px 0px' : '0px'}
                  >
                    Datos del contacto
                  </LabelMedium>
                  <InstallationContactForm />
                </FlexColumn>
              </FlexColumn>
            </FlexResponsive>
          )}
        </>
      )}
    </FlexColumn>
  );
}
